// A proposed change's review rules (SCR-312; REQ-112, REQ-113, ADR-062), pure and DOM-free: each
// risk flag in words with the phrase it asks for, whether the phrases typed so far confirm every
// flag, the approve request (the digest as shown and only the phrases the person typed; nothing
// is ever prefilled), the expiry countdown, a diff value in words, and what each refusal and
// failure code means and what to do next. The server computes the flags and checks the phrases;
// this only mirrors its rule (domain.Confirmation.Matches) so Approve can wait for them.
//
// No uuid is ever shown (AS-1): every server string the card renders goes through `withoutIds`.
import type { components } from "../../api/schema";
import { withoutIds } from "./model";

export type AgentChange = components["schemas"]["AgentChange"];
export type AgentChangeStatus = components["schemas"]["AgentChangeStatus"];
export type AgentRiskFlag = components["schemas"]["AgentRiskFlag"];
export type AgentRiskFlagName = components["schemas"]["AgentRiskFlagName"];
export type AgentDiffEntry = components["schemas"]["AgentDiffEntry"];
export type ApproveAgentChangeRequest = components["schemas"]["ApproveAgentChangeRequest"];

/** The API's bound on a rejection reason (`RejectAgentChangeRequest.reason`, 1–500). */
export const MAX_REJECT_REASON = 500;

/** The help topics the review names; each shell holds a topic per id. */
export const APPROVAL_HELP = {
  confirmation: "assistant.confirmation",
  rejectReason: "assistant.rejectReason",
} as const;

export const APPROVAL_HELP_IDS: readonly string[] = Object.values(APPROVAL_HELP);

// ---------------------------------------------------------------------------------------------
// Flags and their phrases

/** Each REQ-113 flag in words, as the card titles it. */
export const FLAG_LABELS: Readonly<Record<AgentRiskFlagName, string>> = {
  sign_in_last_method: "Removes the last way to sign in",
  revokes_keys: "Revokes keys",
  deletes: "Deletes something",
  disables_models: "Turns models off",
  cross_provider: "Sends requests to another provider",
  guardrails_off: "Turns guardrails off",
  quota_zero: "Leaves a quota at zero",
  admin_access: "Changes who can administer",
  "admin_access.onboarding_mode": "Changes how admins are added",
  bulk: "Changes many things at once",
};

/** What to type, in words: the phrase itself is shown beside it for the person to type. */
export function phraseHint(flag: AgentRiskFlag): string {
  switch (flag.confirmationKind) {
    case "name":
      return "Type the name to confirm:";
    case "count":
      return "Type how many to confirm:";
    case "word":
      return "Type the word to confirm:";
  }
}

/** The person's phrases so far, by flag name. */
export type TypedPhrases = Readonly<Partial<Record<AgentRiskFlagName, string>>>;

/** The server's rule: the typed phrase, trimmed, equals the expected one exactly (case counts). */
export function phraseMatches(flag: AgentRiskFlag, typed: string | undefined): boolean {
  return flag.expected !== "" && (typed ?? "").trim() === flag.expected;
}

/** Whether every flag's phrase is typed (true for a change without flags). */
export function allConfirmed(flags: readonly AgentRiskFlag[], typed: TypedPhrases): boolean {
  return flags.every((flag) => phraseMatches(flag, typed[flag.name]));
}

/**
 * The approve request: the digest exactly as the card holds it, and only the phrases the person
 * typed (trimmed; an empty one is left out). No phrase is ever taken from `expected`.
 */
export function approveRequest(change: AgentChange, typed: TypedPhrases): ApproveAgentChangeRequest {
  const confirmation: Record<string, string> = {};
  for (const flag of change.flags) {
    const phrase = (typed[flag.name] ?? "").trim();
    if (phrase !== "") confirmation[flag.name] = phrase;
  }
  return Object.keys(confirmation).length === 0 ? { artifactDigest: change.digest } : { artifactDigest: change.digest, confirmation };
}

/**
 * The reason as the API takes it, trimmed. `RejectAgentChangeRequest.reason` is 1–500 characters
 * and the API refuses any control character (domain.validateRejectReason), while a <textarea>
 * answers Enter with one: so each run of line breaks and tabs, with the spaces around it, is
 * folded into one space before the reason is checked or sent.
 */
export function rejectReasonText(reason: string): string {
  return reason.replace(/ *[\t\n\v\f\r\u0085\u2028\u2029]+ */g, " ").trim();
}

/** What the API refuses in a reason once breaks are folded: Unicode Cc, as Go's unicode.IsControl. */
const CONTROL = /\p{Cc}/u;

/**
 * A reason the API accepts (1–500 characters, counted as the API counts them, once folded and
 * trimmed, without a control character), or why not: always about the reason, never the change.
 */
export function rejectReasonProblem(reason: string): string | null {
  const text = rejectReasonText(reason);
  if (text === "") return "Say why, so the Assistant can revise it.";
  if (CONTROL.test(text)) return "Take out the hidden characters, which pasted text sometimes carries.";
  if ([...text].length > MAX_REJECT_REASON) return `Keep it to ${MAX_REJECT_REASON} characters.`;
  return null;
}

// ---------------------------------------------------------------------------------------------
// Status and expiry

export const CHANGE_STATUS_LABELS: Readonly<Record<AgentChangeStatus, string>> = {
  proposed: "Waiting for approval",
  approved: "Applying",
  applying: "Applying",
  applied: "Applied",
  failed: "Failed",
  rejected: "Rejected",
  expired: "Expired",
};

/**
 * The status a person sees: a proposal past its hour reads expired before the server says so, and
 * so does a proposal in an ended conversation (ending lapses its pending changes, and FLOW-026 opens
 * an ended session read-only).
 */
export function effectiveStatus(change: AgentChange, nowMs: number, ended = false): AgentChangeStatus {
  if (change.status !== "proposed") return change.status;
  if (ended) return "expired";
  const expires = Date.parse(change.expiresAt);
  return !Number.isNaN(expires) && nowMs >= expires ? "expired" : "proposed";
}

/** How far along a change is; a read never moves a change back down this order. */
const STATUS_RANK: Readonly<Record<AgentChangeStatus, number>> = {
  proposed: 0,
  approved: 1,
  applying: 2,
  applied: 3,
  failed: 3,
  rejected: 3,
  expired: 3,
};

/** The newer of two reads of one change: the further status wins; at the same rank, the incoming read. */
export function laterChange(held: AgentChange | undefined, incoming: AgentChange): AgentChange {
  return held !== undefined && STATUS_RANK[held.status] > STATUS_RANK[incoming.status] ? held : incoming;
}

/**
 * The changes held merged with a read of some or all of them (`incoming` oldest first), by id: a
 * change the read lacks is kept, and an older answer never moves a change back to an earlier
 * status, so the order answers arrive in does not matter. Oldest proposal first; a tie keeps the
 * order the changes were first seen in.
 */
export function mergeChanges(held: readonly AgentChange[], incoming: readonly AgentChange[]): AgentChange[] {
  const byId = new Map(held.map((c) => [c.id, c] as const));
  for (const change of incoming) byId.set(change.id, laterChange(byId.get(change.id), change));
  const at = (c: AgentChange) => {
    const ms = Date.parse(c.proposedAt);
    return Number.isNaN(ms) ? 0 : ms;
  };
  return [...byId.values()].sort((a, b) => at(a) - at(b));
}

/** "Expires in 42 minutes", from the change's expiry. */
export function expiryText(change: AgentChange, nowMs: number): string {
  const left = Date.parse(change.expiresAt) - nowMs;
  if (Number.isNaN(left)) return "Expires within the hour";
  if (left <= 0) return "Expired";
  const minutes = Math.ceil(left / 60_000);
  return minutes <= 1 ? "Expires in less than a minute" : `Expires in ${minutes} minutes`;
}

// ---------------------------------------------------------------------------------------------
// The diff in words

/** A diff value as text: absent is "none"; a list is comma-separated; an id it holds is masked. */
export function diffValue(value: unknown): string {
  if (value === null || value === undefined) return "none";
  if (typeof value === "string") return withoutIds(value);
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value) && value.every((v) => typeof v === "string" || typeof v === "number")) return value.length === 0 ? "none" : withoutIds(value.join(", "));
  return withoutIds(JSON.stringify(value));
}

// ---------------------------------------------------------------------------------------------
// Refusals and failures, in words

export interface ApprovalProblem {
  title: string;
  /** What happened and what to do next. */
  text: string;
  /**
   * Whether the change is read again after this refusal, so the card shows what the server
   * recorded: a route's refusal ends the change `failed`, so Approve must not stay on offer. Only
   * `artifact_changed` (Reload asks first), `confirmation_required` and a refused reason (nothing
   * moved) leave the card as shown.
   */
  reload: boolean;
  /** Whether nothing final was said (the network, a 429, a 5xx), so Try again is offered. */
  retryable?: boolean;
}

const PROBLEMS: Readonly<Record<string, ApprovalProblem>> = {
  artifact_changed: {
    title: "This change was updated",
    text: "The change is no longer the one on your screen. Reload it and review it again before you approve.",
    reload: false,
  },
  target_changed: {
    title: "Changed since it was proposed",
    text: "What this change edits was changed by someone else after the Assistant proposed it, so nothing was written. Ask the Assistant to propose it again.",
    reload: true,
  },
  change_expired: {
    title: "This proposal expired",
    text: "A proposal can be approved for an hour. Ask the Assistant to propose it again.",
    reload: true,
  },
  change_not_proposed: {
    title: "Already decided",
    text: "This change was approved or rejected already. Its outcome is shown on the card.",
    reload: true,
  },
  confirmation_required: {
    title: "Confirmation needed",
    text: "Type each confirmation exactly as shown, then approve again.",
    reload: false,
  },
  session_terminal: {
    title: "This conversation has ended",
    text: "A change in an ended conversation can no longer be approved or rejected, and nothing was written. Start a new conversation and ask the Assistant to propose it again if it is still needed.",
    reload: true,
  },
  apply_interrupted: {
    title: "Interrupted",
    text: "Applying the change was interrupted before it could be checked. Look at the setting itself, then ask the Assistant to propose the change again if it is still needed.",
    reload: true,
  },
  apply_failed: {
    title: "Refused",
    text: "The setting's own screen refused the change without saying why, and nothing was written. Ask the Assistant what went wrong, or make the change on its own screen.",
    reload: true,
  },
  readback_failed: {
    title: "Made, but not verified",
    text: "The change was written, but reading it back afterwards did not answer, so Nexus could not confirm what is now set. Look at the setting itself before asking the Assistant for anything more; do not propose the same change again.",
    reload: true,
  },
  forbidden: {
    title: "Not allowed",
    text: "Your sign-in may not make this change. Ask someone who can to approve it.",
    reload: true,
  },
  not_customer_editable: {
    title: "Not editable here",
    text: "AllCode manages this setting for the customer, so it cannot be changed from here.",
    reload: true,
  },
};

/** A refusal or failure code in words; an unknown code is named, with what to do. */
export function approvalProblem(code: string): ApprovalProblem {
  return (
    PROBLEMS[code] ?? {
      title: "Not applied",
      text: `The change was refused (${withoutIds(code)}). Ask the Assistant what went wrong, or make the change on its own screen.`,
      reload: true,
    }
  );
}

/**
 * An answer that is not a decision: the request never got one (the network), or the API asked to
 * be tried again (a 429, a 5xx). Nothing says the change was refused, so the card offers Try again;
 * approving the same change twice applies it once (a repeat with the same digest is a no-op).
 */
export const TRANSIENT_PROBLEM: ApprovalProblem = {
  title: "That did not go through",
  text: "Nexus could not answer just now, so the change may not have been decided yet. Try again in a moment.",
  reload: true,
  retryable: true,
};

/** A reject whose reason the API refused (422 `validation_failed`): the reason's fault, never the change's. */
export const REASON_REFUSED: ApprovalProblem = {
  title: "The reason was not accepted",
  text: `Write the reason as plain text of up to ${MAX_REJECT_REASON} characters, then reject again. The change is still waiting for a decision.`,
  reload: false,
};

/** Whether an answer is transient rather than a decision: no HTTP answer, a 429 or a 5xx. */
export function isTransient(status: number): boolean {
  return status === 0 || status === 429 || status >= 500;
}
